import {
  ActionPayload,
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import { z } from 'zod/v3';
import { DatasetModel } from '../model/dataset.model';
import { Assert } from '@cotera/utilities';
import { QueryClient } from '@tanstack/react-query';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import { err, ok } from 'neverthrow';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import { toast } from '@cotera/client/app/components/ui';
import { DATASET_ACTION_GROUP } from './constants';
import { v4 } from 'uuid';

type Params = {};

export class RunOnSourceAction extends BaseAction<Params, { t: string }[]> {
  title = 'Refresh Data';
  inputSchema = z.object({});
  group = DATASET_ACTION_GROUP;
  icon = 'play';
  description = 'Run the query on the warehouse (not a sample)';

  constructor(
    private readonly props: {
      queries: QueryClient;
      artifacts: Pick<
        TenantedClient['artifacts'],
        'invalidateArtifact' | 'usableCache'
      >;
      orgId: string;
    }
  ) {
    super();
  }

  /** Dataset must be targeted and exist in graph. */
  applicable = (context: ApplicableContext) => {
    const dataset = context.targetedModelOfType(DatasetModel);
    if (dataset === null) {
      return false;
    }

    return dataset.currentNameInGraph.snapshot() !== null;
  };

  execute = async (
    _: ActionPayload,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string }[]>> => {
    const dataset = context.targetedModelOfType(DatasetModel);
    Assert.assert(dataset !== null, 'Dataset not found');

    const datasetName = dataset.currentNameInGraph.snapshot();
    Assert.assert(datasetName !== null); // by applicable

    const symbol = dataset.graph.snapshot().forSymbol(datasetName);
    Assert.assert(symbol !== null, 'Symbol not found');

    const node = symbol.builtNode;
    Assert.assert(node.isOk());
    const { item } = node.value;

    Assert.assert(item.t === 'rel');

    const useableCache = Assert.assertOk(
      await this.props.artifacts.usableCache({})
    );

    dataset.compile({
      setPreview: true,
      select: (rel) =>
        rel
          .select((t) => ({
            ...t.star(),
            __cache_buster: v4(),
          }))
          .select((t) => t.except('__cache_buster')),
    });

    const id = useableCache[item.rel.sqlHash()]?.id;

    if (id === undefined) {
      toast.error('Artifact ID not found');
      return err([{ t: 'Artifact ID not found' }]);
    }

    const actionToast = toast.loading('Running on source...');

    await this.props.artifacts.invalidateArtifact({ id });

    await this.props.queries.invalidateQueries({
      queryKey: queryKeys.artifacts.usableCache({ orgId: this.props.orgId }),
    });
    await this.props.queries.invalidateQueries({
      queryKey: queryKeys.artifacts.requests({
        orgId: this.props.orgId,
        page: 1,
      }),
    });
    await this.props.queries.invalidateQueries({
      queryKey: queryKeys.artifacts.requestById({
        orgId: this.props.orgId,
        reqId: id,
      }),
    });

    await this.props.queries.invalidateQueries({
      predicate: (query) => {
        if (
          typeof query.state.data === 'object' &&
          query.state.data !== null &&
          'fromArtifactId' in query.state.data
        ) {
          return query.state.data.fromArtifactId === id;
        }
        return false;
      },
    });

    actionToast.success('Refresh Data completed');

    return ok({});
  };
}
